import React from 'react';
import { 
  BookOpen, Users, Star, ClipboardCheck, GraduationCap, 
  Search, Filter, MoreVertical, MessageCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { Topbar } from '../components/Topbar';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Skeleton, SearchInput } from '../components/ui';
import { motion } from 'framer-motion';

export function ProfessorClassesPage() {
  const { user } = useAuth();
  const data = useData();
  const navigate = useNavigate();
  const loading = useLoading(500);

  if (!user || user.type !== 'professor' || !data) return null;

  const { allEnrollments, calculateClassFrequency } = data;

  // Filtrar cursos que o professor leciona
  const profCourses = user.courses ? user.courses.split(',').map(c => c.trim()).filter(c => c !== '') : [];
  
  // Criar estrutura de Turma A e Turma B para cada curso
  const myClasses = [];
  profCourses.forEach(courseName => {
    myClasses.push({
      id: `${courseName}-A`,
      name: `${courseName} - Turma A`,
      course: courseName,
      turma: 'A',
      students: (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'A').length,
      frequency: calculateClassFrequency(courseName, 'A'),
      status: 'Ativa',
      semester: '2026/1'
    });
    myClasses.push({
      id: `${courseName}-B`,
      name: `${courseName} - Turma B`,
      course: courseName,
      turma: 'B',
      students: (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'B').length,
      frequency: calculateClassFrequency(courseName, 'B'),
      status: 'Ativa',
      semester: '2026/1'
    });
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />
      
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-black">Minhas Turmas</h2>
          <p className="text-muted font-medium">Gestão detalhada das turmas sob sua responsabilidade docente.</p>
        </div>
        <div className="flex gap-4">
           <button 
            onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}
            className="flex items-center gap-3 bg-[#5865F2]/10 border border-[#5865F2]/20 px-4 py-2 rounded-2xl hover:bg-[#5865F2]/20 transition-all cursor-pointer"
          >
            <MessageCircle size={18} className="text-[#5865F2]" />
            <span className="text-sm font-bold text-[#5865F2]">Discord Docente</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          [1, 2].map(i => <Skeleton key={i} className="h-64 rounded-[32px]" />)
        ) : myClasses.length > 0 ? (
          myClasses.map(classe => (
            <Card key={classe.id} className="group hover:border-purple/30 transition-all">
              <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                <div className="w-20 h-20 bg-purple/10 rounded-3xl flex items-center justify-center text-purple group-hover:bg-purple group-hover:text-white transition-all shrink-0">
                  <GraduationCap size={40} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-2xl font-black">{classe.name}</h3>
                    <Badge variant="green">Ativa</Badge>
                  </div>
                  <p className="text-muted font-bold text-sm uppercase tracking-widest">{classe.semester} • Turma Presencial</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-muted font-black uppercase tracking-wider">Total de Alunos</span>
                      <strong className="text-lg">{allEnrollments.filter(e => e.course === classe.course).length || classe.students}</strong>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-muted font-black uppercase tracking-wider">Frequência Média</span>
                      <strong className="text-lg text-green">{classe.frequency}%</strong>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-muted font-black uppercase tracking-wider">Próxima Aula</span>
                      <strong className="text-lg">Segunda, 19:00</strong>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-muted font-black uppercase tracking-wider">Sala</span>
                      <strong className="text-lg font-mono">LAB-0{classe.id}</strong>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 w-full md:w-auto">
                  <Button className="w-full md:px-8 py-3" onClick={() => navigate('/meus-alunos')}>
                    <Users size={18} className="mr-2" /> Ver Alunos
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <div className="text-center py-20 bg-white/5 rounded-[40px] border-2 border-dashed border-white/5">
            <BookOpen size={64} className="mx-auto text-muted mb-6 opacity-20" />
            <h3 className="text-xl font-bold mb-2">Nenhuma turma encontrada</h3>
            <p className="text-muted max-w-sm mx-auto">
              Você ainda não possui turmas vinculadas ao seu registro docente. Entre em contato com a coordenação.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
